import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  type BoxProps,
  HStack,
  Icon,
  type IconProps,
  type StackProps,
  Text,
  type TextProps,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  isSubsectionCollapsed,
  type ProfileWindowEntity,
  setSubsectionCollapsed,
} from "../../profileWindowSettings/profileWindowSettings.store";
import { entityProfileModalBorders, entityProfileModalColors } from "../entityProfileModal.theme";

type Props = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Which window this card belongs to. Supplying it opts the card into the
   * "save subsection fold" preference; without it the card is always expanded
   * on mount, as before.
   */
  entity?: ProfileWindowEntity;
  id: string;
  idPrefix?: string;
  isHidden?: boolean;
  title: string;
  titleBadge?: React.ReactNode;
};

const cardShellProps: BoxProps = {
  py: 3,
};

const headerProps: StackProps = {
  bg: entityProfileModalColors.panelHeaderBg,
  borderBottom: entityProfileModalBorders.default,
  justifyContent: "space-between",
  minH: "50px",
  position: "sticky",
  px: 4,
  py: 2.5,
  top: 0,
  w: "full",
  zIndex: 2,
};

const titleButtonProps: StackProps = {
  _hover: { color: entityProfileModalColors.hoverTitle },
  as: "button",
  flex: 1,
  minW: 0,
};

const titleGroupProps: StackProps = {
  alignItems: "center",
  minW: 0,
  spacing: 3,
};

const titleProps: TextProps = {
  color: entityProfileModalColors.title,
  fontSize: "22px",
  fontWeight: 600,
  lineHeight: "1.2",
};

const actionsProps: StackProps = {
  flexShrink: 0,
  spacing: 3,
};

const titleBadgeProps: BoxProps = {
  flexShrink: 0,
};

const collapseButtonProps: BoxProps = {
  _hover: { color: entityProfileModalColors.title },
  alignItems: "center",
  as: "button",
  bg: "transparent",
  color: entityProfileModalColors.secondaryText,
  display: "flex",
  flexShrink: 0,
  h: "28px",
  justifyContent: "center",
  w: "28px",
};

const collapseIconProps: IconProps = {
  boxSize: 5,
};

const contentProps: BoxProps = {
  px: 6,
};

export default function EntityProfileSectionCard(props: Props) {
  const { entity, id } = props;
  // Read once on mount: the stored value is the starting state, not a binding —
  // toggling the card must not be fought by a re-read, and the store already
  // returns `false` for everything while the preference is off.
  const disclosure = useDisclosure({
    defaultIsOpen: entity === undefined || !isSubsectionCollapsed(entity, id),
  });
  const idPrefix = props.idPrefix ?? "react-entity-section";
  const contentId = `${idPrefix}-${props.id}-content`;
  const { isOpen, onToggle } = disclosure;

  const handleToggle = React.useCallback(() => {
    onToggle();

    if (entity !== undefined) {
      setSubsectionCollapsed(entity, id, isOpen);
    }
  }, [entity, id, isOpen, onToggle]);

  return (
    <Box
      display={props.isHidden ? "none" : undefined}
      id={`${idPrefix}-${props.id}`}
      {...cardShellProps}
    >
      <HStack mb={disclosure.isOpen ? 4 : 0} {...headerProps}>
        <HStack
          aria-controls={contentId}
          aria-expanded={disclosure.isOpen}
          {...titleButtonProps}
          type="button"
          onClick={handleToggle}
        >
          <HStack {...titleGroupProps}>
            <Text {...titleProps}>{props.title}</Text>
            {props.titleBadge ? <Box {...titleBadgeProps}>{props.titleBadge}</Box> : null}
          </HStack>
        </HStack>
        <HStack {...actionsProps}>
          {props.actions ? <Box>{props.actions}</Box> : null}
          <Box {...collapseButtonProps} type="button" onClick={handleToggle}>
            <Icon as={disclosure.isOpen ? ChevronUpIcon : ChevronDownIcon} {...collapseIconProps} />
          </Box>
        </HStack>
      </HStack>
      {disclosure.isOpen ? (
        <Box id={contentId} {...contentProps}>
          {props.children}
        </Box>
      ) : null}
    </Box>
  );
}
